/**
 * LenderSubmission - POLISHED VERSION
 * Final review and submission
 */
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Alert, Spinner } from '../../ui/components';
import { PartyPopper, CheckCircle, AlertCircle, Send, ArrowLeft } from 'lucide-react';
import { setCurrentStep } from '../../store/loanApplicationSlice';
import ApplyLayout from '../layout/ApplyLayout';
import { FormSection } from '../forms';
import { supabase } from '../../lib/supabase';

const LenderSubmission = () => {
  const dispatch = useDispatch();
  const applicationData = useSelector((state) => state.loanApplication.applicationData);
  const recommendedProduct = useSelector((state) => state.loanApplication.recommendedProduct);
  const readinessScore = useSelector((state) => state.loanApplication.readinessScore);
  const productRecommendation = useSelector((state) => state.loanApplication.productRecommendation);
  const user = useSelector((state) => state.loanApplication.user);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Check if user is authenticated
      let userId = null;
      
      // First try to get Supabase user
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      if (supabaseUser) {
        userId = supabaseUser.id;
      } else if (user?.email) {
        // For mock auth, create or get user profile
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', user.email)
          .single();
        
        if (existingProfile) {
          userId = existingProfile.id;
        } else {
          // Create a mock user profile for demo purposes
          const { data: newProfile, error: profileError } = await supabase
            .from('profiles')
            .insert({
              email: user.email,
              first_name: user.firstName,
              last_name: user.lastName,
              role: 'applicant',
            })
            .select()
            .single();
          
          if (profileError) {
            console.error('Profile creation error:', profileError);
            // Continue anyway with a placeholder
            userId = 'mock-user-' + Date.now();
          } else {
            userId = newProfile.id;
          }
        }
      }
      
      // Save application to database
      const applicationPayload = {
        applicant_id: userId,
        status: 'cleared_for_marketplace',
        readiness_score: readinessScore || 75,
        recommended_product_id: recommendedProduct?.id,
        purchase_or_refinance: productRecommendation.purchaseOrRefinance,
        property_type: productRecommendation.propertyType,
        property_use: productRecommendation.propertyUse,
        estimated_value: productRecommendation.estimatedValue ? parseInt(productRecommendation.estimatedValue) : null,
        estimated_credit_score: productRecommendation.creditScore,
        down_payment: productRecommendation.downPayment ? parseInt(productRecommendation.downPayment) : null,
        first_time_buyer: productRecommendation.firstTimeBuyer === 'yes',
        // Application data
        first_name: applicationData.firstName,
        last_name: applicationData.lastName,
        email: applicationData.email || user?.email,
        phone: applicationData.phone,
        property_address: applicationData.propertyAddress,
        purchase_price: applicationData.purchasePrice ? parseInt(applicationData.purchasePrice) : null,
        annual_income: applicationData.annualIncome ? parseInt(applicationData.annualIncome) : null,
      };
      
      const { data: savedApp, error: saveError } = await supabase
        .from('loan_applications')
        .insert(applicationPayload)
        .select()
        .single();
      
      if (saveError) {
        console.error('Error saving application:', saveError);
        throw new Error('Failed to save application');
      }
      
      console.log('Application saved successfully:', savedApp);
      
      // Simulate additional processing
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    dispatch(setCurrentStep(4)); // Back to lender selection
  };

  if (isSubmitted) {
    return (
      <ApplyLayout
        step={6}
        totalSteps={9}
        title="Application Submitted!"
        subtitle="Your application has been sent to lenders"
        context="submission"
        showProgress={false}
      >
        <div style={{ textAlign: 'center', padding: 'var(--lk-space-12) 0' }}>
          <PartyPopper size={64} color="var(--lk-success-500)" style={{ marginBottom: 'var(--lk-space-6)' }} />
          <h2 style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>
            Congratulations!
          </h2>
          <p style={{ fontSize: 'var(--lk-text-md)', color: 'var(--lk-text-tertiary)', marginBottom: 'var(--lk-space-8)' }}>
            Your application has been successfully submitted to the lender network.
          </p>

          <div style={{
            padding: 'var(--lk-space-6)', backgroundColor: 'var(--lk-primary-50)',
            borderRadius: 'var(--lk-radius-lg)', marginBottom: 'var(--lk-space-8)',
            border: '1px solid var(--lk-primary-200)',
          }}>
            <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 500, marginBottom: 'var(--lk-space-3)' }}>
              What Happens Next?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)', textAlign: 'left' }}>
              {['Lenders will review your application within 24-48 hours',
                "You'll receive rate quotes via email",
                'Compare offers and choose the best one for you',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-2)' }}>
                  <CheckCircle size={16} color="var(--lk-primary-500)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 'var(--lk-text-sm)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <Button variant="primary" size="lg" onClick={() => window.location.href = '/borrower/dashboard'}>
            Go to Dashboard
          </Button>
        </div>
      </ApplyLayout>
    );
  }

  const SummaryRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--lk-space-1) 0' }}>
      <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>{label}</span>
      <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 500 }}>{value}</span>
    </div>
  );

  return (
    <ApplyLayout
      step={6}
      totalSteps={9}
      title="Review & Submit"
      subtitle="Review your application before submitting to lenders"
      context="submission"
    >
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" icon={AlertCircle} style={{ marginBottom: 'var(--lk-space-5)' }}>
          {error}
        </Alert>
      )}

      {/* Readiness Score */}
      <div style={{
        padding: 'var(--lk-space-6)', backgroundColor: 'var(--lk-primary-50)',
        borderRadius: 'var(--lk-radius-lg)', marginBottom: 'var(--lk-space-8)',
        border: '1px solid var(--lk-primary-200)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', marginBottom: 'var(--lk-space-2)' }}>
          Your Readiness Score
        </div>
        <div style={{ fontSize: '48px', fontWeight: 700, color: 'var(--lk-primary-600)', marginBottom: 'var(--lk-space-2)' }}>
          {readinessScore || 75}
        </div>
        <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>
          {readinessScore >= 80 ? 'Excellent' : readinessScore >= 60 ? 'Good' : 'Fair'} - Ready to submit
        </div>
      </div>

      {/* Application Summary */}
      <FormSection title="Application Summary">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
          <SummaryRow label="Loan Product" value={recommendedProduct?.name || 'Conventional Loan'} />
          <SummaryRow label="Applicant" value={`${applicationData.firstName} ${applicationData.lastName}`} />
          <SummaryRow label="Property Address" value={applicationData.propertyAddress || 'Not provided'} />
          <SummaryRow label="Purchase Price" value={`$${(applicationData.purchasePrice || 0).toLocaleString()}`} />
          <SummaryRow label="Down Payment" value={`$${(applicationData.downPayment || 0).toLocaleString()}`} />
          <SummaryRow label="Annual Income" value={`$${(applicationData.annualIncome || 0).toLocaleString()}`} />
        </div>
      </FormSection>

      {/* Terms & Conditions */}
      <div style={{
        padding: 'var(--lk-space-5)', backgroundColor: 'var(--lk-bg-surface-2)',
        borderRadius: 'var(--lk-radius-lg)', border: '1px solid var(--lk-border)',
        marginTop: 'var(--lk-space-8)',
      }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-3)', cursor: 'pointer' }}>
          <input type="checkbox" id="terms" required style={{ marginTop: 3, accentColor: 'var(--lk-primary-500)' }} />
          <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)', lineHeight: 'var(--lk-leading-relaxed)' }}>
            I agree to the <a href="#" style={{ color: 'var(--lk-text-link)' }}>Terms & Conditions</a> and authorize 
            LoanKnow to share my application with lenders in the network. I understand that lenders may 
            contact me regarding my application.
          </span>
        </label>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 'var(--lk-space-3)', marginTop: 'var(--lk-space-10)' }}>
        <Button variant="secondary" size="lg" icon={ArrowLeft} onClick={handleBack} disabled={isSubmitting} style={{ minWidth: 120 }}>
          Back
        </Button>
        <Button variant="primary" size="lg" icon={isSubmitting ? null : Send} onClick={handleSubmit} disabled={isSubmitting} style={{ flex: 1 }}>
          {isSubmitting ? <><Spinner size="sm" style={{ marginRight: 8 }} /> Submitting...</> : 'Submit to Lenders'}
        </Button>
      </div>
    </ApplyLayout>
  );
};

export default LenderSubmission;
